import { useState } from 'react';
import type { AppSnapshot } from '@/call/app-controller';
import { formatRemaining } from '@/call/call-duration';
import type { Translator } from '@/i18n';
import { AdvancedControls } from './AdvancedControls';
import { CallControls } from './CallControls';
import { DraggablePreview } from './DraggablePreview';
import { InvitePanel } from './InvitePanel';
import { Icon } from './Icon';
import { InviteCard } from './InviteCard';
import { VideoSurface } from './VideoSurface';

interface Props {
  t: Translator['t'];
  snapshot: AppSnapshot;
  onToggleMic: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleMirror: () => void;
  onToggleFit: () => void;
  onToggleBlock: () => void;
  onHangUp: () => void;
  onDismissError: () => void;
  errorText: string | null;
  /** Кімнату створено в цій вкладці: у порожній кімнаті — картка запрошення. */
  createdHere: boolean;
}

export function CallScreen({
  t,
  snapshot,
  onToggleMic,
  onToggleVideo,
  onToggleScreenShare,
  onToggleMirror,
  onToggleFit,
  onToggleBlock,
  onHangUp,
  onDismissError,
  errorText,
  createdHere,
}: Props): React.JSX.Element {
  const [controlsVisible, setControlsVisible] = useState(true);
  const [advancedVisible, setAdvancedVisible] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // Бот-заглушка показується як стан пошуку, а не як співрозмовник.
  const searching =
    snapshot.phase === 'waiting' ||
    snapshot.phase === 'waiting-with-bot' ||
    snapshot.phase === 'connecting';

  return (
    <main className="call">
      <div
        className="stage"
        onClick={() => {
          // Дотик по відео ховає панель; якщо відкрита розширена — спершу її.
          if (advancedVisible) setAdvancedVisible(false);
          else setControlsVisible(!controlsVisible);
        }}
      >
        {snapshot.remoteStream ? (
          <VideoSurface
            stream={snapshot.remoteStream}
            muted={false}
            mirrored={snapshot.mirrorRemote}
            className={`stage__remote stage__remote--${snapshot.remoteFit}`}
            onBlockedAutoplay={setAutoplayBlocked}
          />
        ) : (
          <div className="stage__placeholder">
            <Icon name="account_circle" size={110} />
          </div>
        )}

        {autoplayBlocked && (
          <button
            type="button"
            className="stage__unblock"
            onClick={(event) => {
              event.stopPropagation();
              setAutoplayBlocked(false);
            }}
          >
            <Icon name="play_arrow" size={34} />
          </button>
        )}

        {/* Хост у порожній кімнаті бачить, що робити далі; гість — що чекаємо.
            Поки на екрані помилка зʼєднання, запрошення лише заважало б. */}
        {snapshot.phase === 'waiting' &&
        createdHere &&
        snapshot.roomId !== null &&
        errorText === null ? (
          <InviteCard t={t} roomId={snapshot.roomId} />
        ) : (
          searching && <p className="stage__status stage__status--below">{t('waiting')}</p>
        )}

        {snapshot.phase === 'connected' && (
          <div className="peer-state">
            {!snapshot.peerMedia.micEnabled && (
              <span className="chip">
                <Icon name="mic_off" size={15} /> {t('mic')}
              </span>
            )}
            {!snapshot.peerMedia.videoEnabled && (
              <span className="chip">
                <Icon name="videocam_off" size={15} /> {t('video')}
              </span>
            )}
          </div>
        )}

        {snapshot.phase === 'reconnecting' && (
          <p className="stage__status">{t('connectionLost')}</p>
        )}

        {/* Локальне прев'ю завжди без звуку: інакше користувач чує сам себе. */}
        <DraggablePreview stream={snapshot.localStream} />

        <div
          className={
            snapshot.remainingMs === 0 ? 'stage__timer stage__timer--expired' : 'stage__timer'
          }
        >
          <Icon name={snapshot.remainingMs === 0 ? 'warning' : 'timer'} size={18} />
          <span className="stage__timer-value">
            {snapshot.remainingMs === 0 ? t('timeExpired') : formatRemaining(snapshot.remainingMs)}
          </span>
          {snapshot.remainingMs > 0 && (
            <span className="stage__timer-label">{t('remaining')}</span>
          )}
        </div>

        {snapshot.roomId !== null && (
          <div className="stage__invite">
            <InvitePanel t={t} roomId={snapshot.roomId} />
          </div>
        )}
      </div>

      {errorText !== null && (
        <button type="button" className="banner banner--error" onClick={onDismissError}>
          {errorText}
        </button>
      )}

      {controlsVisible && advancedVisible && (
        <AdvancedControls
          t={t}
          mirrorRemote={snapshot.mirrorRemote}
          remoteFit={snapshot.remoteFit}
          remoteControlBlocked={snapshot.remoteControlBlocked}
          onToggleMirror={onToggleMirror}
          onToggleFit={onToggleFit}
          onToggleBlock={onToggleBlock}
        />
      )}

      {controlsVisible && (
        <CallControls
          t={t}
          micEnabled={snapshot.micEnabled}
          videoEnabled={snapshot.videoEnabled}
          screenSharing={snapshot.screenSharing}
          screenShareSupported={snapshot.screenShareSupported}
          onToggleMic={onToggleMic}
          onToggleVideo={onToggleVideo}
          onToggleScreenShare={onToggleScreenShare}
          advancedVisible={advancedVisible}
          onToggleAdvanced={() => {
            setAdvancedVisible(!advancedVisible);
          }}
          onHangUp={onHangUp}
        />
      )}
    </main>
  );
}
